import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Plus, Trash2, Save, Package, RefreshCw } from 'lucide-react';

const UOM_OPTIONS = [
  { value: 'EA', label: 'Eaches (EA)' },
  { value: 'CA', label: 'Cases (CA)' },
  { value: 'PK', label: 'Packs (PK)' },
  { value: 'PL', label: 'Pallets (PL)' },
];

const ProductMappings = ({ setPage }) => {
  const { token } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL || 'https://api.jaychrisedi.com';

  const [partners, setPartners] = useState([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState('');
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // id of mapping being saved
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [newMapping, setNewMapping] = useState({
    buyerSku: '',
    vendorSku: '',
    description: '',
    unitOfMeasureRetailer: 'EA',
    unitOfMeasureSupplier: 'CA',
    conversionFactor: 12,
  });
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    fetchPartners();
  }, []);

  useEffect(() => {
    if (selectedPartnerId) fetchMappings();
  }, [selectedPartnerId]);

  const headers = { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' };

  const fetchPartners = async () => {
    try {
      const res = await fetch(API_URL + '/api/partners', { headers });
      const data = await res.json();
      setPartners(Array.isArray(data) ? data : []);
      if (data.length > 0) setSelectedPartnerId(data[0].id);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const fetchMappings = async () => {
    try {
      const res = await fetch(API_URL + '/api/product-mappings?partnerId=' + selectedPartnerId, { headers });
      const data = await res.json();
      setMappings(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleAdd = async () => {
    if (!newMapping.buyerSku || !newMapping.vendorSku) {
      setError('Buyer SKU and Vendor SKU are required');
      return;
    }
    setError(null);
    setSaving('new');
    try {
      const res = await fetch(API_URL + '/api/product-mappings', {
        method: 'POST',
        headers,
        body: JSON.stringify({ ...newMapping, partnerId: selectedPartnerId }),
      });
      if (!res.ok) {
        const err = await res.json();
        setError(err.error || 'Failed to add mapping');
        setSaving(null);
        return;
      }
      setNewMapping({ buyerSku: '', vendorSku: '', description: '', unitOfMeasureRetailer: 'EA', unitOfMeasureSupplier: 'CA', conversionFactor: 12 });
      setShowAddForm(false);
      setSuccess('Product mapping added');
      setTimeout(() => setSuccess(null), 3000);
      fetchMappings();
    } catch (err) {
      setError('Network error');
    }
    setSaving(null);
  };

  const handleUpdate = async (mapping) => {
    setSaving(mapping.id);
    setError(null);
    try {
      const res = await fetch(API_URL + '/api/product-mappings/' + mapping.id, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          buyerSku: mapping.buyerSku,
          vendorSku: mapping.vendorSku,
          description: mapping.description,
          unitOfMeasureRetailer: mapping.unitOfMeasureRetailer,
          unitOfMeasureSupplier: mapping.unitOfMeasureSupplier,
          conversionFactor: mapping.conversionFactor,
        }),
      });
      if (res.ok) {
        setSuccess('Mapping updated');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to update mapping');
      }
    } catch (err) {
      setError('Network error');
    }
    setSaving(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product mapping?')) return;
    try {
      await fetch(API_URL + '/api/product-mappings/' + id, { method: 'DELETE', headers });
      setMappings(mappings.filter(m => m.id !== id));
      setSuccess('Mapping deleted');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to delete');
    }
  };

  const updateMappingField = (id, field, value) => {
    setMappings(mappings.map(m => m.id === id ? { ...m, [field]: value } : m));
  };

  const conversionPreview = (retailerUom, supplierUom, factor) => {
    if (!factor || factor <= 0 || retailerUom === supplierUom) return null;
    return `${factor} ${retailerUom} = 1 ${supplierUom}`;
  };

  const S = {
    page: { minHeight: '100vh', background: 'linear-gradient(to bottom, #f8fafc, #e2e8f0)' },
    header: { background: 'linear-gradient(135deg, #0D2B5E 0%, #1A6BB5 100%)', color: 'white', padding: '48px', position: 'relative', overflow: 'hidden' },
    card: { backgroundColor: 'rgba(255,255,255,0.95)', borderRadius: '16px', padding: '32px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0' },
    input: { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '2px solid #e2e8f0', fontSize: '14px', outline: 'none', boxSizing: 'border-box', fontFamily: 'JetBrains Mono, monospace' },
    select: { padding: '10px 12px', borderRadius: '8px', border: '2px solid #e2e8f0', fontSize: '14px', outline: 'none', boxSizing: 'border-box', background: 'white', cursor: 'pointer' },
    label: { display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' },
    btn: { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 20px', borderRadius: '10px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', border: 'none', transition: 'all 0.15s' },
    tag: { display: 'inline-block', padding: '2px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '700', fontFamily: 'JetBrains Mono, monospace' },
  };

  if (loading) {
    return <div style={{ ...S.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><RefreshCw size={32} style={{ animation: 'spin 1s linear infinite' }} /></div>;
  }

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <button onClick={() => setPage('dashboard')} style={{
            backgroundColor: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)',
            color: 'white', padding: '10px 20px', borderRadius: '10px', fontSize: '14px',
            fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px'
          }}>
            <ArrowLeft size={16} /> Back to Dashboard
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '16px', padding: '12px' }}>
              <Package size={32} strokeWidth={2.5} />
            </div>
            <div>
              <h1 style={{ fontSize: '36px', fontWeight: '900', marginBottom: '8px', letterSpacing: '-1px' }}>Product Mappings</h1>
              <p style={{ fontSize: '16px', opacity: 0.85 }}>Map SKUs and configure unit-of-measure conversions per trading partner</p>
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '-32px auto 0', padding: '0 48px 48px', position: 'relative', zIndex: 1 }}>
        {/* Alerts */}
        {error && (
          <div style={{ padding: '12px 16px', borderRadius: '10px', marginBottom: '16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', fontSize: '14px', fontWeight: '600' }}>
            {error}
          </div>
        )}
        {success && (
          <div style={{ padding: '12px 16px', borderRadius: '10px', marginBottom: '16px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', fontSize: '14px', fontWeight: '600' }}>
            {success}
          </div>
        )}

        {/* Partner selector + Add button */}
        <div style={{ ...S.card, marginBottom: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <div>
              <label style={S.label}>Trading Partner</label>
              <select
                value={selectedPartnerId}
                onChange={e => setSelectedPartnerId(e.target.value)}
                style={{ ...S.select, minWidth: '220px' }}
              >
                {partners.length === 0 && <option value="">No partners configured</option>}
                {partners.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div style={{ fontSize: '14px', color: '#64748b', alignSelf: 'flex-end', paddingBottom: '4px' }}>
              {mappings.length} mapping{mappings.length !== 1 ? 's' : ''}
            </div>
          </div>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            style={{ ...S.btn, background: showAddForm ? '#e2e8f0' : 'linear-gradient(135deg, #10b981, #059669)', color: showAddForm ? '#475569' : 'white', boxShadow: showAddForm ? 'none' : '0 4px 12px rgba(16,185,129,0.3)' }}
          >
            <Plus size={16} strokeWidth={3} /> {showAddForm ? 'Cancel' : 'Add Mapping'}
          </button>
        </div>

        {/* Add new mapping form */}
        {showAddForm && (
          <div style={{ ...S.card, marginBottom: '24px', borderLeft: '4px solid #10b981' }}>
            <div style={{ fontSize: '18px', fontWeight: '800', color: '#0f172a', marginBottom: '20px' }}>New Product Mapping</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '20px' }}>
              <div>
                <label style={S.label}>Retailer SKU (Buyer)</label>
                <input value={newMapping.buyerSku} onChange={e => setNewMapping({ ...newMapping, buyerSku: e.target.value })} placeholder="e.g., WMT-12345" style={S.input} />
              </div>
              <div>
                <label style={S.label}>Your SKU (Vendor)</label>
                <input value={newMapping.vendorSku} onChange={e => setNewMapping({ ...newMapping, vendorSku: e.target.value })} placeholder="e.g., JC-WIDGET-BLK" style={S.input} />
              </div>
              <div>
                <label style={S.label}>Description</label>
                <input value={newMapping.description} onChange={e => setNewMapping({ ...newMapping, description: e.target.value })} placeholder="Product description" style={{ ...S.input, fontFamily: 'Inter, sans-serif' }} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '20px' }}>
              <div>
                <label style={S.label}>Retailer Orders In</label>
                <select value={newMapping.unitOfMeasureRetailer} onChange={e => setNewMapping({ ...newMapping, unitOfMeasureRetailer: e.target.value })} style={{ ...S.select, width: '100%' }}>
                  {UOM_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label style={S.label}>You Ship In</label>
                <select value={newMapping.unitOfMeasureSupplier} onChange={e => setNewMapping({ ...newMapping, unitOfMeasureSupplier: e.target.value })} style={{ ...S.select, width: '100%' }}>
                  {UOM_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label style={S.label}>Conversion Factor</label>
                <input type="number" min="1" step="1" value={newMapping.conversionFactor} onChange={e => setNewMapping({ ...newMapping, conversionFactor: parseFloat(e.target.value) || 1 })} style={S.input} />
                {conversionPreview(newMapping.unitOfMeasureRetailer, newMapping.unitOfMeasureSupplier, newMapping.conversionFactor) && (
                  <div style={{ fontSize: '12px', color: '#0D2B5E', fontWeight: '600', marginTop: '6px', padding: '4px 8px', background: '#EFF6FF', borderRadius: '6px', display: 'inline-block' }}>
                    {conversionPreview(newMapping.unitOfMeasureRetailer, newMapping.unitOfMeasureSupplier, newMapping.conversionFactor)}
                  </div>
                )}
              </div>
            </div>
            {/* Live example */}
            {newMapping.unitOfMeasureRetailer !== newMapping.unitOfMeasureSupplier && newMapping.conversionFactor > 1 && (
              <div style={{ padding: '12px 16px', borderRadius: '10px', backgroundColor: '#FFFBEB', border: '1px solid #FDE68A', marginBottom: '16px', fontSize: '13px', color: '#92400E' }}>
                <strong>Example:</strong> If the PO says {newMapping.conversionFactor * 10} {newMapping.unitOfMeasureRetailer}, the system will show {10} {newMapping.unitOfMeasureSupplier} for fulfillment. The ASN and invoice will send back {newMapping.conversionFactor * 10} {newMapping.unitOfMeasureRetailer} to match the PO.
              </div>
            )}
            <button onClick={handleAdd} disabled={saving === 'new'} style={{ ...S.btn, background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white', opacity: saving === 'new' ? 0.7 : 1 }}>
              <Save size={16} /> {saving === 'new' ? 'Saving...' : 'Save Mapping'}
            </button>
          </div>
        )}

        {/* Existing mappings */}
        {mappings.length === 0 && !showAddForm && (
          <div style={{ ...S.card, textAlign: 'center', padding: '60px 32px' }}>
            <Package size={48} style={{ color: '#94a3b8', marginBottom: '16px' }} />
            <div style={{ fontSize: '20px', fontWeight: '700', color: '#0f172a', marginBottom: '8px' }}>No product mappings yet</div>
            <div style={{ fontSize: '15px', color: '#64748b', marginBottom: '24px' }}>
              Add mappings to configure SKU translation and unit-of-measure conversion for this partner.
            </div>
            <button onClick={() => setShowAddForm(true)} style={{ ...S.btn, background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white' }}>
              <Plus size={16} strokeWidth={3} /> Add First Mapping
            </button>
          </div>
        )}

        {mappings.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {mappings.map(m => (
              <div key={m.id} style={{ ...S.card, padding: '20px 24px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px', alignItems: 'end' }}>
                  <div>
                    <label style={S.label}>Retailer SKU</label>
                    <input value={m.buyerSku} onChange={e => updateMappingField(m.id, 'buyerSku', e.target.value)} style={S.input} />
                  </div>
                  <div>
                    <label style={S.label}>Your SKU</label>
                    <input value={m.vendorSku} onChange={e => updateMappingField(m.id, 'vendorSku', e.target.value)} style={S.input} />
                  </div>
                  <div>
                    <label style={S.label}>Description</label>
                    <input value={m.description} onChange={e => updateMappingField(m.id, 'description', e.target.value)} style={{ ...S.input, fontFamily: 'Inter, sans-serif' }} />
                  </div>
                  <div>
                    <label style={S.label}>Retailer UOM</label>
                    <select value={m.unitOfMeasureRetailer} onChange={e => updateMappingField(m.id, 'unitOfMeasureRetailer', e.target.value)} style={{ ...S.select, width: '100%' }}>
                      {UOM_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={S.label}>Supplier UOM</label>
                    <select value={m.unitOfMeasureSupplier} onChange={e => updateMappingField(m.id, 'unitOfMeasureSupplier', e.target.value)} style={{ ...S.select, width: '100%' }}>
                      {UOM_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={S.label}>Factor</label>
                    <input type="number" min="1" step="1" value={m.conversionFactor} onChange={e => updateMappingField(m.id, 'conversionFactor', parseFloat(e.target.value) || 1)} style={{ ...S.input, maxWidth: '100px' }} />
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button onClick={() => handleUpdate(m)} disabled={saving === m.id} title="Save changes" style={{ ...S.btn, padding: '10px 14px', background: '#0D2B5E', color: 'white', opacity: saving === m.id ? 0.7 : 1 }}>
                      <Save size={14} />
                    </button>
                    <button onClick={() => handleDelete(m.id)} title="Delete mapping" style={{ ...S.btn, padding: '10px 14px', background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca' }}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                {conversionPreview(m.unitOfMeasureRetailer, m.unitOfMeasureSupplier, m.conversionFactor) && (
                  <div style={{ marginTop: '10px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <span style={{ ...S.tag, backgroundColor: '#EFF6FF', color: '#0D2B5E' }}>
                      {conversionPreview(m.unitOfMeasureRetailer, m.unitOfMeasureSupplier, m.conversionFactor)}
                    </span>
                    <RefreshCw size={12} style={{ color: '#94a3b8' }} />
                    <span style={{ fontSize: '12px', color: '#64748b' }}>
                      PO: {m.conversionFactor * 10} {m.unitOfMeasureRetailer} → Fulfillment: {10} {m.unitOfMeasureSupplier}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default ProductMappings;
